import { useEffect, useRef, useState } from 'react'

/**
 * The QR encodes the public ticket page — of one ticket, or of a door pass
 * covering all of a buyer's tickets to a concert. The unguessable hash in the
 * URL is the secret: guests scanning it see the event info online, while the
 * door scanner extracts the same hash to verify and mark the tickets used.
 */
function verificationUrl(hash: string) {
	return `${window.location.origin}/t/${hash}`
}

async function toDataUrl(hash: string): Promise<string> {
	const QRCode = await import('qrcode')
	return QRCode.default.toDataURL(verificationUrl(hash), {
		margin: 1,
		width: 200,
		errorCorrectionLevel: 'Q',
	})
}

/**
 * Renders a QR code that door staff can scan to look up a ticket (or door
 * pass) by its cryptographic `hash`. The code is generated lazily in the
 * browser so we don't pay the qrcode library cost until a QR is shown.
 */
export function TicketQr({
	hash,
	label,
	className,
}: {
	hash: string
	/** alt text; defaults to the ticket's serial */
	label?: string
	className?: string
}) {
	const [dataUrl, setDataUrl] = useState<string | null>(null)
	const cancelled = useRef(false)

	useEffect(() => {
		cancelled.current = false
		toDataUrl(hash)
			.then((url) => {
				if (!cancelled.current) setDataUrl(url)
			})
			.catch((error) => console.error('Failed to generate ticket QR:', error))
		return () => {
			cancelled.current = true
		}
	}, [hash])

	if (!dataUrl) {
		return (
			<div
				className={`h-12 w-12 ${className ?? ''}`}
				aria-label="Lastar QR..."
			/>
		)
	}

	return (
		<img
			src={dataUrl}
			alt={label ?? `QR-kode for billett ${hash.slice(0, 8)}`}
			className={className}
		/>
	)
}
